import { useState } from "react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Clock } from "lucide-react";

export function tsToSeconds(value) {
  if (!value) return null;
  const parts = String(value).trim().split(":").map((p) => parseInt(p, 10));
  if (parts.some((n) => isNaN(n))) return null;
  if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  if (parts.length === 2) return parts[0] * 60 + parts[1];
  if (parts.length === 1) return parts[0];
  return null;
}

function Timestamp({ value, onSeek }) {
  if (!value) return null;
  const seconds = tsToSeconds(value);
  if (seconds == null || !onSeek) {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] font-mono text-primary mb-1.5">
        <Clock className="h-3 w-3" /> {value}
      </span>
    );
  }
  return (
    <button
      type="button"
      onClick={() => onSeek(seconds)}
      data-testid={`timestamp-${seconds}`}
      title="Vai a questo punto del video"
      className="inline-flex items-center gap-1 text-[11px] font-mono text-primary mb-1.5 rounded-sm px-1 -ml-1 hover:bg-primary hover:text-primary-foreground transition-colors"
    >
      <Clock className="h-3 w-3" /> {value}
    </button>
  );
}

export default function BilingualView({ paragraphs = [], originalLanguage = "Originale", onSeek }) {
  const [hover, setHover] = useState(-1);

  if (!paragraphs.length) {
    return <p className="text-sm text-muted-foreground">Testo non ancora disponibile.</p>;
  }

  return (
    <div data-testid="bilingual-view">
      {/* Desktop: two-column aligned rows */}
      <div className="hidden md:block">
        <div className="grid grid-cols-2 gap-8 pb-3 mb-2 border-b border-border">
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-muted-foreground">
            Testo originale{originalLanguage && originalLanguage !== "Originale" ? ` · ${originalLanguage}` : ""}
          </div>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-primary">
            Traduzione italiana
          </div>
        </div>
        <div className="divide-y divide-border">
          {paragraphs.map((p, i) => (
            <div
              key={i}
              data-testid={`bilingual-row-${i}`}
              onMouseEnter={() => setHover(i)}
              onMouseLeave={() => setHover(-1)}
              className="grid grid-cols-2 gap-8 py-5 rounded-sm transition-colors"
              style={{ backgroundColor: hover === i ? "hsl(var(--muted) / 0.6)" : "transparent" }}
            >
              <div>
                <Timestamp value={p.timestamp} onSeek={onSeek} />
                <p className="text-base leading-relaxed text-muted-foreground italic">{p.original}</p>
              </div>
              <div>
                <p className="text-base leading-relaxed text-foreground">{p.italian}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Mobile: tabbed view */}
      <div className="md:hidden">
        <Tabs defaultValue="it">
          <TabsList className="grid grid-cols-2 w-full rounded-sm">
            <TabsTrigger value="it" data-testid="bilingual-tab-it" className="rounded-sm">
              Italiano
            </TabsTrigger>
            <TabsTrigger value="orig" data-testid="bilingual-tab-orig" className="rounded-sm">
              {originalLanguage}
            </TabsTrigger>
          </TabsList>
          <TabsContent value="it" className="space-y-4 mt-4">
            {paragraphs.map((p, i) => (
              <div key={i}>
                <Timestamp value={p.timestamp} onSeek={onSeek} />
                <p className="text-base leading-relaxed">{p.italian}</p>
              </div>
            ))}
          </TabsContent>
          <TabsContent value="orig" className="space-y-4 mt-4">
            {paragraphs.map((p, i) => (
              <div key={i}>
                <Timestamp value={p.timestamp} onSeek={onSeek} />
                <p className="text-base leading-relaxed text-muted-foreground italic">{p.original}</p>
              </div>
            ))}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
